import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Target, Send, CheckCircle2, ExternalLink, Gauge } from "lucide-react";
import { toast } from "sonner";
import { apiJson } from "@/lib/api";
import ThinkingPulse from "@/components/ThinkingPulse";
import ObjectifSections from "@/components/value/ObjectifSections";
import SocleSection from "@/components/value/SocleSection";
import type { Socle } from "@/components/value/SocleSection";
import BoucleDiagram from "@/components/value/BoucleDiagram";
import type { ObjView } from "@/components/value/ObjectifSections";

interface Doc { activite?: string; cible?: string; offre?: string; prix_moyen_ht?: number; objectif_ca_eur?: number; horizon_mois?: number; ressources?: string; contraintes?: string; points_a_confirmer?: string[]; taux_conversion?: number; taux_horaire?: number }
interface Equation { clients_necessaires: number; prospects_necessaires: number; taux_conversion: number; taux_conversion_est_hypothese: boolean; ca_mensuel_cible: number | null; clients_par_mois: number | null; note: string }
interface Action { key: string; titre: string; detail: string; livrable: boolean; minutes: number; lien?: string; statut: string }
interface Roi { livrables: number; heures_evitees: number; taux_horaire: number; taux_horaire_est_hypothese: boolean; valeur_estimee_eur: number; lignes: { titre: string; date: string; minutes: number }[]; note: string }
interface Msg { role: string; content: string }
interface View extends Partial<ObjView> { socle?: Socle; doc: Doc; equation: Equation | null; messages: Msg[]; actions: Action[]; roi: Roi }

const eur = (v?: number | null) => (v || v === 0 ? `${Math.round(v).toLocaleString("fr-FR")} €` : "—");
const STATUT: Record<string, string> = { propose: "Proposé", accepte: "Accepté, à planifier", livre: "Livré", refuse: "Refusé" };

export default function MonPlan() {
  const [v, setV] = useState<View | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [deciding, setDeciding] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { setV(await apiJson<View>("/api/value/plan")); } catch { toast.error("Impossible de charger votre plan."); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [v?.messages.length, busy]);

  const send = async () => {
    const m = input.trim();
    if (!m || busy) return;
    setInput(""); setBusy(true);
    setV((cur) => (cur ? { ...cur, messages: [...cur.messages, { role: "user", content: m }] } : cur));
    try { setV(await apiJson<View>("/api/value/chat", { method: "POST", body: JSON.stringify({ message: m }) })); }
    catch { toast.error("Grafton n'a pas pu répondre, réessayez."); }
    finally { setBusy(false); }
  };

  const saveField = async (patch: Partial<Doc>) => {
    try { setV(await apiJson<View>("/api/value/doc", { method: "PUT", body: JSON.stringify(patch) })); } catch { toast.error("Enregistrement impossible"); }
  };

  const decide = async (key: string, decision: "accepte" | "refuse") => {
    setDeciding(key);
    try { setV(await apiJson<View>(`/api/value/actions/${key}`, { method: "POST", body: JSON.stringify({ decision }) })); toast.success(decision === "accepte" ? (key === "site" || key === "page_vitrine" ? "Votre site est prêt à relire" : "Pris en compte") : "Noté"); }
    catch (e) { toast.error((e as Error)?.message?.includes("activité") ? "Décrivez d'abord votre activité et votre offre à Grafton." : "Action impossible, réessayez."); }
    finally { setDeciding(""); }
  };

  const d = v?.doc || {};
  const field = (label: string, key: keyof Doc, type: "text" | "number" = "text", multiline = false) => (
    <div className="space-y-1" key={key}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {multiline ? (
        <Textarea defaultValue={(d[key] as string) || ""} key={`${key}-${d[key] ?? ""}`} rows={2} className="text-sm" onBlur={(e) => e.target.value !== (d[key] || "") && saveField({ [key]: e.target.value })} />
      ) : (
        <Input defaultValue={d[key] !== undefined ? String(d[key]) : ""} key={`${key}-${d[key] ?? ""}`} type={type} className="h-8 text-sm" placeholder="À renseigner pendant l'échange"
          onBlur={(e) => { const val = type === "number" ? (e.target.value === "" ? undefined : Number(e.target.value)) : e.target.value; if (val !== undefined && val !== d[key]) void saveField({ [key]: val }); }} />
      )}
    </div>
  );

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          <header className="h-14 flex items-center gap-3 border-b border-border px-4">
            <SidebarTrigger /><Target className="h-5 w-5 text-primary" /><h1 className="text-lg font-semibold">Mon plan : définir et atteindre mon objectif</h1>
          </header>
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 space-y-6">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-muted-foreground">
              En quelques questions, Grafton définit avec vous un objectif de chiffre d'affaires et remplit votre document de cadrage au fur et à mesure. Il s'engage sur les moyens mis en œuvre, pas sur un résultat : aucun chiffre d'affaires n'est promis.
            </div>

            <BoucleDiagram active={!v?.doc?.objectif_ca_eur ? 1 : v?.socle && !v.socle.peut_deployer ? 2 : !v?.strategie ? 3 : 4} />

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="flex flex-col">
                <CardHeader className="pb-2"><CardTitle className="text-base">Échange avec Grafton</CardTitle><CardDescription>Une question à la fois. Répondez comme vous le feriez à un associé.</CardDescription></CardHeader>
                <CardContent className="flex-1 flex flex-col gap-3">
                  <div className="flex-1 min-h-[320px] max-h-[460px] overflow-y-auto space-y-3 pr-1" data-testid="plan-chat">
                    {v && v.messages.length === 0 && <p className="text-sm text-muted-foreground">Dites-moi quelle activité vous souhaitez lancer ou développer, et nous construisons votre plan ensemble.</p>}
                    {v?.messages.map((m, i) => (
                      <div key={i} className={`text-sm rounded-lg px-3 py-2 max-w-[90%] ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-secondary/60 border border-border/50"}`}>{m.content}</div>
                    ))}
                    {busy && <ThinkingPulse />}
                    <div ref={endRef} />
                  </div>
                  <div className="flex gap-2">
                    <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} className="text-sm" placeholder="Votre réponse…" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} data-testid="plan-input" />
                    <Button onClick={send} disabled={busy || !input.trim()} size="icon" className="self-end"><Send className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Votre document de cadrage</CardTitle><CardDescription>Il se remplit pendant l'échange. Vous pouvez corriger chaque champ.</CardDescription></CardHeader>
                <CardContent className="space-y-3">
                  {field("Activité", "activite")}
                  {field("Cible (qui achète)", "cible")}
                  {field("Offre", "offre", "text", true)}
                  <div className="grid grid-cols-3 gap-3">
                    {field("Prix moyen HT (€)", "prix_moyen_ht", "number")}
                    {field("Objectif de CA (€)", "objectif_ca_eur", "number")}
                    {field("Horizon (mois)", "horizon_mois", "number")}
                  </div>
                  {field("Ressources (temps, budget)", "ressources", "text", true)}
                  {field("Contraintes", "contraintes", "text", true)}
                  {v?.equation && (
                    <div className="rounded-lg border border-border bg-secondary/30 p-3 text-sm space-y-1" data-testid="plan-equation">
                      <p className="font-medium">L'équation de votre objectif</p>
                      <p>{v.equation.clients_necessaires} ventes à {eur(d.prix_moyen_ht)} pour atteindre {eur(d.objectif_ca_eur)}{v.equation.clients_par_mois ? `, soit environ ${v.equation.clients_par_mois} par mois` : ""}.</p>
                      <p className="text-muted-foreground">Avec un taux de conversion de {Math.round(v.equation.taux_conversion * 100)} %{v.equation.taux_conversion_est_hypothese ? " (hypothèse à confirmer)" : ""}, il faut environ {v.equation.prospects_necessaires.toLocaleString("fr-FR")} prospects.</p>
                      <p className="text-xs text-muted-foreground">{v.equation.note}</p>
                    </div>
                  )}
                  {d.points_a_confirmer && d.points_a_confirmer.length > 0 && (
                    <div className="text-xs text-amber-500"><p className="font-medium">À confirmer</p><ul className="list-disc pl-4">{d.points_a_confirmer.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
                  )}
                </CardContent>
              </Card>
            </div>

            {v?.socle && <SocleSection socle={v.socle} onChange={(sc) => setV((cur) => (cur ? { ...cur, socle: sc } : cur))} />}

            {v && <ObjectifSections v={{ doc: v.doc, strategie: v.strategie ?? null, revues: v.revues ?? [], actions_statut: v.actions_statut ?? {}, avancement: v.avancement ?? null, autorisations: v.autorisations, deploiement: v.deploiement, socle: v.socle }} onUpdate={(nv) => setV((cur) => (cur ? { ...cur, ...nv, doc: { ...cur.doc, ...nv.doc } } : cur))} />}

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Que souhaitez-vous que Grafton exécute ?</CardTitle><CardDescription>Rien n'est lancé sans votre accord, point par point.</CardDescription></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-3">
                {v?.actions.map((a) => (
                  <div key={a.key} className="rounded-lg border border-border p-3 space-y-2" data-testid={`action-${a.key}`}>
                    <div className="flex items-start justify-between gap-2"><p className="font-medium text-sm">{a.titre}</p><Badge variant="outline" className="text-[10px] shrink-0">{STATUT[a.statut] || a.statut}</Badge></div>
                    <p className="text-xs text-muted-foreground">{a.detail}</p>
                    <div className="flex flex-wrap gap-2">
                      {deciding === a.key && <ThinkingPulse size="sm" label="Grafton prépare…" />}
                      {a.statut === "propose" && deciding !== a.key && <><Button size="sm" onClick={() => decide(a.key, "accepte")}>{a.livrable ? "Produire maintenant" : "Oui, exécuter"}</Button><Button size="sm" variant="ghost" onClick={() => decide(a.key, "refuse")}>Non merci</Button></>}
                      {a.statut === "accepte" && a.lien && <Button size="sm" variant="outline" onClick={() => navigate(a.lien!)}><ExternalLink className="h-3.5 w-3.5 mr-1" />Ouvrir l'outil</Button>}
                      {a.statut === "livre" && <><span className="text-xs text-emerald-500 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Première version prête</span>{a.lien && <Button size="sm" variant="outline" onClick={() => navigate(a.lien!)}><ExternalLink className="h-3.5 w-3.5 mr-1" />Voir mon site</Button>}</>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {v && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Gauge className="h-4 w-4 text-primary" />Ce que Grafton a exécuté pour vous</CardTitle><CardDescription>Mesure de l'exécution, pas une promesse de résultat.</CardDescription></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="rounded-lg border border-border p-3"><p className="text-2xl font-bold">{v.roi.livrables}</p><p className="text-xs text-muted-foreground">livrable(s)</p></div>
                    <div className="rounded-lg border border-border p-3"><p className="text-2xl font-bold">{v.roi.heures_evitees} h</p><p className="text-xs text-muted-foreground">de travail évité (estimé)</p></div>
                    <div className="rounded-lg border border-border p-3"><p className="text-2xl font-bold">{eur(v.roi.valeur_estimee_eur)}</p><p className="text-xs text-muted-foreground">valeur estimée à {v.roi.taux_horaire} €/h{v.roi.taux_horaire_est_hypothese ? " (hypothèse)" : ""}</p></div>
                  </div>
                  {v.roi.lignes.length > 0 && <ul className="text-xs text-muted-foreground space-y-0.5">{v.roi.lignes.map((l, i) => <li key={i}>{new Date(l.date).toLocaleDateString("fr-FR")} : {l.titre} (≈ {Math.round(l.minutes / 60)} h évitées)</li>)}</ul>}
                  <p className="text-xs text-muted-foreground">{v.roi.note}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
